// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/tracking/ShipmentsList.tsx
// 
// تغییرات این نسخه:
// - 🆕 نمایش invoice_number (شماره اینوایس) در ردیف
// - 🆕 نمایش notes (توضیحات) در ردیف
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { trackingAPI } from "../../services/api";
import { usePermission } from "../../hooks/usePermission";
import { gregorianToJalaliStr } from "../../utils/PersianDatePicker";
import { Plus, Eye, Edit2, Trash2, Search, Truck, Filter } from "lucide-react";
import ShipmentForm from "./ShipmentForm";
import ShipmentProfile from "./ShipmentProfile";

const fmt = (n: any) => new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 3 }).format(Number(n) || 0);

const STATUS_LABELS: Record<string, string> = {
  in_customs: "در گمرک",
  clearing:   "در حال ترخیص",
  cleared:    "ترخیص شده",
  distributed:"توزیع شده",
};

const STATUS_COLORS: Record<string, string> = {
  in_customs: "#f59e0b",
  clearing:   "#3b82f6",
  cleared:    "#06b6d4",
  distributed:"#10b981",
};

export default function ShipmentsList() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const canAdd    = usePermission("production", "add");
  const canEdit   = usePermission("production", "edit");
  const canDelete = usePermission("production", "delete");

  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [viewId, setViewId] = useState<number | null>(null);
  const [toast, setToast] = useState({ text: "", ok: true });

  const load = useCallback(async () => {
    setLoading(true);
    const r = await trackingAPI.listShipments();
    if (r.success) setList(r.shipments || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (text: string, ok = true) => {
    setToast({ text, ok });
    setTimeout(() => setToast({ text: "", ok: true }), 3000);
  };

  const handleDelete = async (id: number, serial: string) => {
    if (!window.confirm(`حذف بار ${serial}؟\n\nاین عمل تمام توزیع‌ها و تأثیرات روی موجودی گدام را برمی‌گرداند.`)) return;
    const r = await trackingAPI.deleteShipment(id);
    if (r.success) { showToast("بار حذف شد"); load(); }
    else showToast(r.message || "خطا", false);
  };

  const filtered = list.filter((s: any) => {
    if (statusFilter !== "all" && s.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const fields = [s.serial, s.bar_number, s.invoice_number, s.customs_office_name, s.transit_owner, s.bl_number, s.i_number, s.e_number, s.container_number, s.truck_plate, s.notes].filter(Boolean).join(" ").toLowerCase();
      if (!fields.includes(q)) return false;
    }
    return true;
  });

  if (showForm || editId) {
    return (
      <ShipmentForm
        shipmentId={editId}
        onBack={() => { setShowForm(false); setEditId(null); load(); }}
      />
    );
  }

  if (viewId) {
    return (
      <ShipmentProfile
        shipmentId={viewId}
        onBack={() => { setViewId(null); load(); }}
        onEdit={(id) => { setViewId(null); setEditId(id); }}
      />
    );
  }

  const counts = {
    all: list.length,
    in_customs: list.filter((s:any) => s.status === "in_customs").length,
    clearing:   list.filter((s:any) => s.status === "clearing").length,
    cleared:    list.filter((s:any) => s.status === "cleared").length,
    distributed:list.filter((s:any) => s.status === "distributed").length,
  };

  return (
    <div dir="rtl" style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
      {toast.text && (
        <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 9999, padding: "12px 24px", borderRadius: 12, background: toast.ok ? "#10b981" : "#ef4444", color: "#fff", fontWeight: 700, fontSize: 13, boxShadow: "0 4px 20px rgba(0,0,0,0.25)" }}>
          {toast.text}
        </div>
      )}

      {/* جستجو + دکمه */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: 1, minWidth: 240, position: "relative" }}>
          <Search size={15} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: T.subtext }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="جستجو در بارها (سریال، شماره بار، اینوایس، توضیحات، ...)"
            style={{
              width: "100%", padding: "11px 38px 11px 14px", borderRadius: 10,
              border: `1.5px solid ${T.cardBorder}`,
              background: dark ? "rgba(255,255,255,0.05)" : "#fff",
              color: T.text, fontSize: 13, fontFamily: "inherit",
              direction: "rtl", outline: "none", boxSizing: "border-box",
            }}
          />
        </div>
        {canAdd && (
          <button onClick={() => setShowForm(true)} style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "11px 22px", borderRadius: 10, border: "none",
            background: "linear-gradient(135deg,#3b82f6,#2563eb)", color: "#fff",
            cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
            boxShadow: "0 4px 12px rgba(59,130,246,0.35)",
            whiteSpace: "nowrap",
          }}>
            <Plus size={15} /> بار جدید
          </button>
        )}
      </div>

      {/* تب‌های فیلتر */}
      <div style={{ display: "flex", gap: 4, padding: 5, marginBottom: 16, borderRadius: 12, background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)", border: `1px solid ${T.cardBorder}`, overflowX: "auto" }}>
        {[
          { id: "all",         label: "همه",         count: counts.all,         color: "#64748b" },
          { id: "in_customs",  label: "در گمرک",     count: counts.in_customs,  color: STATUS_COLORS.in_customs },
          { id: "clearing",    label: "در حال ترخیص", count: counts.clearing,    color: STATUS_COLORS.clearing },
          { id: "cleared",     label: "ترخیص شده",   count: counts.cleared,     color: STATUS_COLORS.cleared },
          { id: "distributed", label: "توزیع شده",   count: counts.distributed, color: STATUS_COLORS.distributed },
        ].map(t => {
          const active = statusFilter === t.id;
          return (
            <button key={t.id} onClick={() => setStatusFilter(t.id)} style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "8px 16px", borderRadius: 9, border: "none",
              cursor: "pointer", fontSize: 12.5, fontWeight: 700, fontFamily: "inherit",
              background: active ? `linear-gradient(135deg,${t.color},${t.color}d4)` : "transparent",
              color: active ? "#fff" : T.subtext,
              boxShadow: active ? `0 2px 8px ${t.color}55` : "none",
              whiteSpace: "nowrap", transition: "all 0.2s",
            }}>
              <span>{t.label}</span>
              <span style={{
                fontSize: 10.5, fontWeight: 800,
                padding: "2px 8px", borderRadius: 8,
                background: active ? "rgba(255,255,255,0.25)" : `${t.color}25`,
                color: active ? "#fff" : t.color,
                minWidth: 20, textAlign: "center",
              }}>{t.count}</span>
            </button>
          );
        })}
      </div>

      {/* لیست */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>در حال بارگذاری...</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: T.subtext, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)", borderRadius: 12 }}>
          <Truck size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
          <div>هیچ باری یافت نشد</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map((s: any) => {
            const color = STATUS_COLORS[s.status];
            return (
              <div key={s.id} style={{
                padding: "14px 18px", borderRadius: 12,
                background: dark ? "rgba(255,255,255,0.03)" : "#fff",
                border: `1px solid ${T.cardBorder}`,
                borderRight: `4px solid ${color}`,
                cursor: "pointer", transition: "transform 0.15s, box-shadow 0.15s",
              }}
                onClick={() => setViewId(s.id)}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 6px 16px ${color}25`; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
              >
                {/* ردیف اول: اطلاعات اصلی */}
                <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                  <span style={{ padding: "4px 10px", borderRadius: 7, background: `${color}20`, color, fontSize: 11, fontWeight: 800, fontFamily: "monospace" }}>
                    {s.serial}
                  </span>

                  <span style={{ padding: "3px 10px", borderRadius: 6, background: `${color}15`, color, fontSize: 10.5, fontWeight: 700 }}>
                    {STATUS_LABELS[s.status]}
                  </span>

                  {s.bar_number && <span style={{ fontSize: 12, color: T.text, fontWeight: 700 }}>📦 {s.bar_number}</span>}

                  {/* 🆕 شماره اینوایس */}
                  {s.invoice_number && (
                    <span style={{ fontSize: 12, color: "#8b5cf6", fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: "rgba(139,92,246,0.1)" }}>
                      🧾 INV: {s.invoice_number}
                    </span>
                  )}

                  <span style={{ fontSize: 12, color: T.subtext }}>
                    🏢 {s.customs_office_name}
                  </span>

                  <span style={{ fontSize: 12, color: T.subtext }}>
                    📅 ثبت: {gregorianToJalaliStr(s.entry_date)}
                  </span>

                  {/* 🆕 تاریخ ترخیص (اگر وجود داشت) */}
                  {s.clearance_date && (
                    <span style={{ fontSize: 12, color: "#06b6d4", fontWeight: 700 }}>
                      ✅ ترخیص: {gregorianToJalaliStr(s.clearance_date)}
                    </span>
                  )}

                  {s.ownership_type === "transit" && (
                    <span style={{ padding: "2px 8px", borderRadius: 5, background: "rgba(168,85,247,0.15)", color: "#a855f7", fontSize: 10, fontWeight: 700 }}>
                      ترانزیت
                    </span>
                  )}

                  {s.container_number && (
                    <span style={{ fontSize: 11, color: T.subtext, fontFamily: "monospace" }}>
                      🚢 {s.container_number}
                    </span>
                  )}

                  {s.truck_plate && (
                    <span style={{ fontSize: 11, color: T.subtext, fontFamily: "monospace" }}>
                      🚚 {s.truck_plate}
                    </span>
                  )}

                  <span style={{ fontSize: 11, color: T.subtext }}>
                    {s.items_count} جنس · {s.distributions_count} توزیع
                  </span>

                  <div style={{ marginRight: "auto", display: "flex", gap: 6, alignItems: "center" }} onClick={e => e.stopPropagation()}>
                    <button onClick={() => setViewId(s.id)} title="مشاهده و مدیریت" style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: "rgba(139,92,246,0.15)", color: "#8b5cf6", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Eye size={14} />
                    </button>
                    {canEdit && (
                      <button onClick={() => setEditId(s.id)} title="ویرایش بار" style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: "rgba(59,130,246,0.15)", color: "#3b82f6", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Edit2 size={14} />
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => handleDelete(s.id, s.serial)} title="حذف بار" style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: "rgba(239,68,68,0.15)", color: "#ef4444", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {/* 🆕 ردیف اقلام: نام جنس + تعداد + واحد */}
                {s.items && Array.isArray(s.items) && s.items.length > 0 && (
                  <div style={{
                    marginTop: 10, padding: "8px 12px",
                    background: dark ? "rgba(59,130,246,0.06)" : "rgba(59,130,246,0.04)",
                    borderRadius: 8,
                    display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
                    borderRight: `2px solid ${color}50`,
                  }}>
                    <span style={{ fontSize: 11, color: T.subtext, fontWeight: 700 }}>📦 اقلام:</span>
                    {s.items.map((it: any, idx: number) => (
                      <span key={idx} style={{
                        fontSize: 11.5,
                        padding: "3px 10px",
                        borderRadius: 6,
                        background: dark ? "rgba(255,255,255,0.06)" : "#fff",
                        color: T.text,
                        border: `1px solid ${T.cardBorder}`,
                      }}>
                        <strong style={{ color: T.text }}>{it.product_name}</strong>
                        <span style={{ color: "#3b82f6", fontWeight: 700, marginRight: 6 }}>
                          {fmt(it.quantity)}{it.unit ? ` ${it.unit}` : ""}
                        </span>
                      </span>
                    ))}
                  </div>
                )}

                {/* 🆕 ردیف دوم: توضیحات (notes) */}
                {s.notes && (
                  <div style={{
                    marginTop: 10, padding: "8px 12px",
                    background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
                    borderRadius: 8,
                    fontSize: 12, color: T.subtext, fontStyle: "italic",
                    borderRight: `2px solid ${color}50`,
                  }}>
                    📝 {s.notes}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}